/**
 * Codex Design System — Color Palettes
 *
 * 17 Norse-named palettes · 13 steps each (0–1000) · 221 total tokens
 * Brand palettes (★): cinnabar, saffron, lapis, tyrian, iris, madder
 *
 * Dark mode step mapping (Figma Foundations §11.1):
 *   Solid fill       500 → 500
 *   Hover            600 → 400
 *   Active/pressed   700 → 300
 *   Text on bg       800–900 → 100–200
 *   Subtle bg        0–50 → 900–950
 *   Borders          200–300 → 700–800
 *   Icons            600 → 400
 */

export const palettes = {
  // Warm ─────────────────────────────────────────────────────────────────────
  /** ★ Vivid crimson — error states, destructive actions */
  cinnabar: {
    0: '#FDF1F3',
    50: '#FCDCE0',
    100: '#FBC6CD',
    200: '#FB97A5',
    300: '#FC677B',
    400: '#FE3752',
    500: '#FF0829',
    600: '#D7011E',
    700: '#A70319',
    800: '#760514',
    900: '#47060E',
    950: '#31050B',
    1000: '#1B0307',
  },
  /** Deep burnt orange — warm CTAs, community, events */
  sienna: {
    0: '#FDF6F2',
    50: '#FAE7DE',
    100: '#F8D9C8',
    200: '#F6BB9C',
    300: '#F59D6F',
    400: '#F47E41',
    500: '#F36014',
    600: '#CD4E0B',
    700: '#9F3E0B',
    800: '#712E0B',
    900: '#441D09',
    950: '#2F1507',
    1000: '#1A0C05',
  },
  /** Warm umber brown — earth tones, vintage/organic UI */
  sepia: {
    0: '#FAF7F5',
    50: '#F2EBE6',
    100: '#EADED6',
    200: '#DBC6B7',
    300: '#CDAD96',
    400: '#BF9476',
    500: '#B17B55',
    600: '#946544',
    700: '#734F37',
    800: '#533A29',
    900: '#33241A',
    950: '#231913',
    1000: '#140E0B',
  },
  /** ★ Rich amber — warning states, pending status */
  saffron: {
    0: '#FDF9F2',
    50: '#FAEFDD',
    100: '#F8E6C8',
    200: '#F7D59B',
    300: '#F6C36D',
    400: '#F6B23F',
    500: '#F5A111',
    600: '#CF8509',
    700: '#A06809',
    800: '#724B09',
    900: '#452E08',
    950: '#2F2007',
    1000: '#1A1204',
  },
  /** Golden yellow — highlights, badges, celebration */
  gilt: {
    0: '#FDFBF2',
    50: '#FBF5DD',
    100: '#F9EFC7',
    200: '#F9E599',
    300: '#F9DC6A',
    400: '#FAD43B',
    500: '#FACA0D',
    600: '#D3AA05',
    700: '#A38406',
    800: '#745E07',
    900: '#463907',
    950: '#302806',
    1000: '#1B1604',
  },

  // Green ────────────────────────────────────────────────────────────────────
  /** Warm chartreuse — trending, freshness, eco */
  verdigris: {
    0: '#FBFBF3',
    50: '#F7F7E1',
    100: '#F3F3CE',
    200: '#ECECA6',
    300: '#E6E67E',
    400: '#E0E055',
    500: '#DADA2D',
    600: '#B7B721',
    700: '#8E8E1C',
    800: '#656516',
    900: '#3E3E0F',
    950: '#2A2A0C',
    1000: '#181807',
  },
  /** True lime — online status, progress, growth */
  malachite: {
    0: '#F6FCF2',
    50: '#E8F9DE',
    100: '#DBF7CA',
    200: '#BFF39F',
    300: '#A2F173',
    400: '#86EF46',
    500: '#6AEC1B',
    600: '#56C711',
    700: '#449A10',
    800: '#326E0E',
    900: '#20420B',
    950: '#162E08',
    1000: '#0D1905',
  },
  /** True emerald — success states, verification */
  laurel: {
    0: '#F3FCF6',
    50: '#E0F8E9',
    100: '#CCF4DB',
    200: '#A3EFBF',
    300: '#79EBA3',
    400: '#4FE687',
    500: '#25E26B',
    600: '#1ABE57',
    700: '#179345',
    800: '#126932',
    900: '#0D4020',
    950: '#0A2C16',
    1000: '#06180D',
  },
  /** Deep teal — creator tools, cool data viz */
  azurite: {
    0: '#F2FCFB',
    50: '#DFF9F7',
    100: '#CAF6F2',
    200: '#9FF3EB',
    300: '#73F0E5',
    400: '#47EEDF',
    500: '#1CEBD9',
    600: '#12C6B6',
    700: '#109A8D',
    800: '#0E6D65',
    900: '#0B423D',
    950: '#082D2A',
    1000: '#051917',
  },

  // Cool ─────────────────────────────────────────────────────────────────────
  /** ★ Cerulean blue — info states, links, data viz */
  lapis: {
    0: '#F3F9FB',
    50: '#E1EFF7',
    100: '#CEE6F3',
    200: '#A6D5EC',
    300: '#7EC3E6',
    400: '#55B2E0',
    500: '#2DA0D9',
    600: '#2285B7',
    700: '#1C688E',
    800: '#164B65',
    900: '#102E3D',
    950: '#0C202A',
    1000: '#071218',
  },
  /** True indigo — focus rings, tertiary actions */
  woad: {
    0: '#F2F6FC',
    50: '#DFE7F9',
    100: '#CAD8F6',
    200: '#A0B9F2',
    300: '#749BF0',
    400: '#487CED',
    500: '#1C5DEA',
    600: '#134BC5',
    700: '#113B99',
    800: '#0F2C6D',
    900: '#0B1C42',
    950: '#09142D',
    1000: '#050C19',
  },

  // Purple & Pink ────────────────────────────────────────────────────────────
  /** ★ Royal purple — primary actions, brand, key UI */
  tyrian: {
    0: '#F5F4FA',
    50: '#E6E4F4',
    100: '#D6D2EE',
    200: '#B7AFE3',
    300: '#968BD9',
    400: '#7667CE',
    500: '#5643C4',
    600: '#4434A4',
    700: '#372A80',
    800: '#29205B',
    900: '#1A1538',
    950: '#130F26',
    1000: '#0B0915',
  },
  /** ★ Luminous violet — focus rings, active/selected states */
  iris: {
    0: '#F5F2FD',
    50: '#E6DEFA',
    100: '#D7C8F8',
    200: '#B89CF5',
    300: '#986FF4',
    400: '#7841F4',
    500: '#5815F2',
    600: '#470CCC',
    700: '#380C9E',
    800: '#2A0B71',
    900: '#1B0944',
    950: '#13072F',
    1000: '#0B051A',
  },
  /** ★ Magenta-purple — secondary actions, premium features */
  madder: {
    0: '#F9F3FB',
    50: '#F1E1F6',
    100: '#E8CEF2',
    200: '#D8A8EA',
    300: '#C980E4',
    400: '#B958DD',
    500: '#A930D6',
    600: '#8D24B4',
    700: '#6E1E8C',
    800: '#4F1864',
    900: '#31103D',
    950: '#220C2A',
    1000: '#130717',
  },
  /** Warm rose pink — reactions, social, premium highlights */
  carmine: {
    0: '#FCF3F7',
    50: '#F8DFEB',
    100: '#F5CBE0',
    200: '#F0A2C8',
    300: '#EC77B0',
    400: '#E84D98',
    500: '#E42380',
    600: '#C0186A',
    700: '#951553',
    800: '#6A113C',
    900: '#400D26',
    950: '#2C0A1A',
    1000: '#19060F',
  },

  // Neutral ──────────────────────────────────────────────────────────────────
  /** Neutral grey — body text, borders, backgrounds, dividers */
  pumice: {
    0: '#F7F7F8',
    50: '#EAEBED',
    100: '#DEDFE3',
    200: '#C4C7CD',
    300: '#ABB0B8',
    400: '#9298A3',
    500: '#78808E',
    600: '#636976',
    700: '#4E525C',
    800: '#393C43',
    900: '#242629',
    950: '#191A1D',
    1000: '#0E0F10',
  },
  /** Blue-grey slate — secondary text, muted backgrounds, code */
  graphite: {
    0: '#F6F7F8',
    50: '#E9EBEE',
    100: '#DCDFE4',
    200: '#C1C7D1',
    300: '#A6B0BE',
    400: '#8A98AB',
    500: '#6F8098',
    600: '#5B697E',
    700: '#485362',
    800: '#343C47',
    900: '#21262C',
    950: '#171A1E',
    1000: '#0D0F11',
  },
} as const;

export type PaletteName = keyof typeof palettes;
export type PaletteStep = 0 | 50 | 100 | 200 | 300 | 400 | 500 | 600 | 700 | 800 | 900 | 950 | 1000;
